import algoliasearch from "algoliasearch";
import { getTeams, getRecruitsInBatches, getRecruit, getHighSchools, getRecruitIdsByEts, getRecruitCount } from "./supabase";
import { delActivityFromIndex } from "./algoliaActivities";
import { convertToUnixTimestamp } from "./utilities";
import { rebuildActivity } from "./algoliaActivities";
import pLimit from 'p-limit'; // If using a concurrency limiter library

// Initialize Algolia client
const client = algoliasearch('ETT8OD4DUG', 'bb501e3aa5c51ecc4515f46406dc894c');

const indexName = 'PROD_RECRUIT_ATHLETE';
const highSchoolsIndexName = 'PROD_HIGH_SCHOOLS';
const BATCH_SIZE = 100; // Size of each batch

// Cache for teams
let teamsCache = null;
let hsCache = null;

async function getHighSchoolsOnce() {
 if (!hsCache) {
   hsCache = await getHighSchools();
 }
 return hsCache
}

async function getTeamsOnce() {
  if (!teamsCache) {
    teamsCache = await getTeams();
  }
  return teamsCache;
}

export async function deleteRecruitFromIndex(recruit) {
  const index = client.initIndex(indexName);
  
  await index.deleteObject(recruit.id);
  if (recruit.Activity && recruit.Activity.length > 0) {
    for (const activity of recruit.Activity) {
      await delActivityFromIndex(activity.id);
    }
  }
  
  return true;
}

export async function rebuildRecruit(id) {
  const recruit = await getRecruit(id);

  if (recruit) {
    const index = client.initIndex(indexName);
    const teams = await getTeamsOnce();
    const highSchools = await getHighSchoolsOnce();

    const newIndexEntry = buildIndexEntry(recruit, teams, highSchools);
    if (newIndexEntry) {
      await index.saveObject(newIndexEntry, { autoGenerateObjectIDIfNotExist: true });
      await rebuildActivity(id);
    }
  }
}

export async function rebuildRecruits() {
  console.log('STARTING REBUILD');
  await clearIndex(indexName);
  await clearIndex('PROD_RECRUIT_ACTIVITIES');

  let batchStart = 0;
  let recruitsBatch = [];
  const limit = pLimit(10); // Limit concurrency to 10 recruits at a time

  do {
    recruitsBatch = await getRecruitsInBatches(batchStart, BATCH_SIZE);

    if (!Array.isArray(recruitsBatch)) {
      console.error('Error fetching recruits batch:', recruitsBatch);
      break;
    }

    console.log(`Fetched ${recruitsBatch.length} recruits starting from ${batchStart}`);

    if (recruitsBatch.length > 0) {
      // Process each recruit in parallel with a concurrency limit
      const promises = recruitsBatch.map(recruit =>
        limit(() => rebuildRecruit(recruit.id))
      );

      try {
        await Promise.all(promises); // Await all recruits in the batch
      } catch (error) {
        console.error('Error rebuilding recruits:', error);
      }
    }

    batchStart += BATCH_SIZE;

  } while (recruitsBatch.length === BATCH_SIZE);

  console.log('FINISHED REBUILD');
}

// Non-destructive version of rebuildRecruits(): refreshes every recruit's
// PROD_RECRUIT_ATHLETE record in place (upsert via rebuildRecruit, same as
// a single-recruit sync) instead of clearIndex()-ing first, so the live
// index is never empty/partial mid-run. Use this to pick up coach data
// (or anything else sourced from HighSchools/Teams) that went stale before
// the HighSchools-edit auto-sync existed. Trade-off vs. the full rebuild:
// this won't remove index entries for recruits that were deleted from
// Supabase or that no longer pass buildIndexEntry's filters -- it only
// touches recruits that currently exist. onProgress(processed, total) is
// optional, called after each batch, for UI progress reporting.
export async function resyncAllRecruits(onProgress) {
  console.log('STARTING NON-DESTRUCTIVE RECRUITS RESYNC');

  // Force fresh HighSchools/Teams data instead of whatever was cached
  // earlier in this session.
  hsCache = null;
  teamsCache = null;

  const total = await getRecruitCount();

  let batchStart = 0;
  let recruitsBatch = [];
  let processed = 0;
  const limit = pLimit(10); // Limit concurrency to 10 recruits at a time

  do {
    recruitsBatch = await getRecruitsInBatches(batchStart, BATCH_SIZE);

    if (!Array.isArray(recruitsBatch)) {
      console.error('Error fetching recruits batch:', recruitsBatch);
      break;
    }

    console.log(`Fetched ${recruitsBatch.length} recruits starting from ${batchStart}`);

    if (recruitsBatch.length > 0) {
      const promises = recruitsBatch.map(recruit =>
        limit(() => rebuildRecruit(recruit.id))
      );

      try {
        await Promise.all(promises);
      } catch (error) {
        console.error('Error resyncing recruits:', error);
      }
    }

    processed += recruitsBatch.length;
    batchStart += BATCH_SIZE;

    if (onProgress) {
      onProgress(processed, total);
    }

  } while (recruitsBatch.length === BATCH_SIZE);

  console.log('FINISHED RECRUITS RESYNC');
  return processed;
}

function heightToInches(height) {
  const [feet, inches] = height.split("'");
  const feetInInches = parseInt(feet, 10) * 12;
  const totalInches = feetInInches + (inches ? parseInt(inches, 10) : 0);
  return totalInches;
}

function buildIndexEntry(recruit, teams, highSchools) {
  if (recruit && recruit.rank && recruit.rank !== 'Q (Questionnaire)' && recruit.id !== 18759) {
    recruit.objectID = recruit.id;
    recruit.displayLocation = (recruit.hsCity ? recruit.hsCity + ", " : '') + (recruit.hsState ? recruit.hsState : '');
    recruit.location = recruit.hsState ? recruit.hsState : 'Unknown';

    const highSchool = highSchools.find(x => x.ETS.toUpperCase() === recruit.ets?.toUpperCase());
    if (highSchool && recruit.ets && recruit.ets.trim() !== '') {
      recruit.highSchool = highSchool.School;
      recruit.hsState = highSchool.State;
      recruit.hsStreet = highSchool.Address;
      recruit.hsCity = highSchool.City;
      recruit.hsZip = highSchool.Zip;
      recruit.hsCounty = highSchool.County;
      recruit.hsPhone = highSchool.Phone;
      recruit.coachName = highSchool.Coach;
      recruit.coachEmail = highSchool.Email;
      recruit.coachPhone = highSchool.Cell;
      recruit.coachTwitter = highSchool.Twitter;
    }

    recruit.isSigned = false;
    recruit.isCommitted = false;
    recruit.hasOffers = false;
    recruit.offerNumber = 0;
    recruit.status = "Uncommitted";
    if (recruit.highSchool) {
      recruit.highSchool = !recruit.highSchool.includes('(' + recruit.location + ')') ? recruit.highSchool + " (" + recruit.location + ")" : recruit.highSchool;
    }

    const limitedInteractions = [];

    if (recruit.Activity && recruit.Activity.length > 0) {
      for (const activity of recruit.Activity) {
        if (activity.teamId !== null) {
          const t = teams.find(x => x.id === activity.teamId);
          if (t) {
            if (activity.interactionTypeId === 4) {
              recruit.isCommitted = true;
              recruit.committedTeam = activity.teamName;
              recruit.committedTeamLogo = activity.logo;
            }
            if (activity.interactionTypeId === 2) {
              recruit.hasOffers = true;
              recruit.offerNumber++;
            }
            
            const newActivity = {
              interactionType: activity.interactionTypeId,
              created_at: activity.activity_at,
              date: convertToUnixTimestamp(activity.activity_at),
              id: t.id,
              name: t.name,
              logo: t.logo,
              conference: t.College?.Conference?.name,
              division: t.College?.Conference?.conferenceDivisionId?.toUpperCase()
            };
            limitedInteractions.push(newActivity);
          } else {
            console.log('Team not found:', activity.teamId);
          }
        }
      }
    }

    if (recruit.isCommitted) {
      recruit.status = "Committed";
    } else if (recruit.hasOffers) {
      recruit.status = "Uncommitted with offers";
    }

    recruit.gpa = recruit.gpa ? parseFloat(recruit.gpa) : 0;
    recruit.weight = recruit.weight ? parseFloat(recruit.weight) : 0;
    recruit.sat = recruit.sat ? parseInt(recruit.sat) : 0;
    recruit.act = recruit.act ? parseInt(recruit.act) : 0;

    recruit.heightInches = recruit.height ? heightToInches(recruit.height) : 0;
    recruit.name = `${recruit.firstName} ${recruit.lastName}`;
    recruit.Activity = limitedInteractions;
    recruit.twitter = recruit.twitter || "Unknown";
    recruit.added = convertToUnixTimestamp(recruit.created_at);

    return recruit;
  }
  return null;
}

export async function clearIndex(indexName) {
  try {
    const index = client.initIndex(indexName);
    await index.clearObjects();
    return true;
  } catch (error) {
    console.log("ERROR: ", error);
    return false;
  }
}

function buildHighSchoolIndexEntry(highSchool) {
  return {
    id: String(highSchool.id),
    ETS: highSchool.ETS,
    School: highSchool.School,
    Address: highSchool.Address,
    City: highSchool.City,
    State: highSchool.State,
    Zip: highSchool.Zip,
    County: highSchool.County,
    Coach: highSchool.Coach,
    Phone: highSchool.Phone,
    Cell: highSchool.Cell,
    Email: highSchool.Email,
    Twitter: highSchool.Twitter,
  };
}

// The PROD_HIGH_SCHOOLS index was originally bulk-loaded through the
// Algolia dashboard, so its existing records carry dashboard-generated
// objectIDs instead of the HighSchools row id. `id` also isn't declared
// filterable, so it can't be used in a `filters` lookup yet. This is a
// one-time, idempotent fix: it's safe to call repeatedly (admin-triggered
// via "Rebuild High Schools Index"), and once it's applied, syncHighSchool
// can reliably find and overwrite the right record instead of creating a
// duplicate.
export async function prepareHighSchoolsIndex() {
  const index = client.initIndex(highSchoolsIndexName);
  const settings = await index.getSettings();
  const attributesForFaceting = settings.attributesForFaceting || [];
  if (!attributesForFaceting.includes('filterOnly(id)')) {
    await index.setSettings({
      attributesForFaceting: [...attributesForFaceting, 'filterOnly(id)'],
    });
  }
  return true;
}

// Syncs a single HighSchools row into the PROD_HIGH_SCHOOLS Algolia index.
// Looks up any existing record for this id first (see prepareHighSchoolsIndex)
// so the write overwrites it in place rather than creating a duplicate;
// falls back to using the HighSchools id as the objectID when no existing
// record is found (new school, or the index hasn't been prepared yet).
export async function syncHighSchool(highSchool) {
  if (!highSchool || !highSchool.id) return false;

  const index = client.initIndex(highSchoolsIndexName);
  const indexEntry = buildHighSchoolIndexEntry(highSchool);
  let objectID = indexEntry.id;

  try {
    const { hits } = await index.search('', { filters: `id:${highSchool.id}` });
    if (hits && hits.length > 0) {
      objectID = hits[0].objectID;
    }
  } catch (error) {
    console.log('Error looking up existing HighSchools index record: ', JSON.stringify(error));
  }

  try {
    await index.saveObject({ ...indexEntry, objectID });
    return true;
  } catch (error) {
    console.log('Error syncing HighSchools index record: ', JSON.stringify(error));
    return false;
  }
}

// One-time/admin backfill: normalizes every HighSchools row into the
// PROD_HIGH_SCHOOLS index (resolving legacy dashboard-generated objectIDs
// along the way). Safe to re-run any time.
export async function rebuildHighSchoolsIndex() {
  await prepareHighSchoolsIndex();
  const highSchools = await getHighSchools();
  const limit = pLimit(10);
  await Promise.all(highSchools.map((hs) => limit(() => syncHighSchool(hs))));
  return true;
}

// Re-syncs every PROD_RECRUIT_ATHLETE record for players at this high
// school (matched by ETS), so a coach/contact edit on a HighSchools row
// shows up in every affected player's index entry, not just the school's
// own PROD_HIGH_SCHOOLS record. rebuildRecruit() re-reads HighSchools
// internally, so clear the module-level cache first or it would just
// re-stamp the stale coach info that was cached before this edit.
export async function resyncRecruitsForHighSchool(ets) {
  if (!ets || ets === "") return true;

  hsCache = null;

  const recruits = await getRecruitIdsByEts(ets);
  if (!recruits || recruits.length === 0) return true;

  const limit = pLimit(10);
  await Promise.all(recruits.map((r) => limit(() => rebuildRecruit(r.id))));
  return true;
}
